'use client';
import { useBunja } from 'bunja/react';
import { isSameMonth, isSameYear } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { Spinner } from '@flipster/react/design-system/spinner';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import CircleStackIcon from '@flipster/react/icon/generated/CircleStackIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { Styles, css } from '@flipster/styled-system/css';
import { center, grid, hstack, stack } from '@flipster/styled-system/patterns';
import { formatDatetime, timestampToDate } from '@flipster/utils/datetime';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { SocialReferralEvent } from '@flipster/utils/segment/events/social-referral';

import { SegmentPage } from '../misc/segment/segment-page';
import { useGetUrlWithAnonymousId } from '../shared/hooks/useGetUrlWithAnonymousId';
import { socialReferralBunja } from './bunja';
import { CompletedPage } from './completed-page';

export const SocialReferralPage = () => {
  const t = useTranslations();
  const router = useRouter();
  const { campaignAtom, isPendingAtom, isCompletedAtom } = useBunja(socialReferralBunja);
  const isPending = useAtomValue(isPendingAtom);
  const isCompleted = useAtomValue(isCompletedAtom);
  const campaign = useAtomValue(campaignAtom);
  useEffect(() => {
    if (isPending || campaign != null) return;
    dialog.open({
      title: {
        label: 'This promotion couldn’t be found',
      },
      content: 'The link may be broken, or the promotion may have already ended.',
      buttons: {
        primary: <Dialog.Button hierarchy='primary' label='OK' onClick={() => router.replace('/')} />,
      },
    });
  }, [router, isPending, campaign]);
  if (isPending || !campaign)
    return (
      <main
        className={center({
          margin: '0 auto',
          maxW: 'desktopMaxWidth',
          minH: 'minHeightWithoutNavigation',
        })}
      >
        <Spinner />
      </main>
    );
  if (isCompleted) return <CompletedPage />;
  return (
    <>
      <SegmentPage name={SocialReferralEvent.SocialReferralPageView} context={{ campaign_id: campaign.campaignId }} />
      <main
        className={css({
          p: 16,
          pb: { tabletDown: 120 },
          margin: '0 auto',
          maxW: { tablet: 'desktopMaxWidth' },
          minH: 'minHeightWithoutNavigation',
        })}
      >
        <div
          className={grid({
            tabletDown: {
              gridTemplateAreas: `
            "title"
            "image"
            "challenge"
            "details"
            "cta"
          `,
            },
            tablet: {
              pt: 80,
              gridTemplateColumns: '1fr 1fr',
              gridAutoRows: 'auto',
              gridTemplateAreas: `
            "title     image"
            "cta       image"
            "challenge challenge"
            "details   details"
          `,
            },
          })}
        >
          <Header css={{ gridArea: 'title' }} />
          <img
            alt='background of social referral campaign'
            src={campaign.webLandingPageContent.mainImageUrl}
            className={css({
              gridArea: 'image',
              m: '0 auto',
              h: 280,
              objectFit: 'contain',
              py: 14,
              tabletDown: { w: '100%', pb: 0 },
            })}
          />
          <div
            className={stack({
              gridArea: 'challenge',
              gap: 16,
              p: 20,
              rounded: 8,
              bg: 'background.default.level-1',
              mt: 24,
            })}
          >
            <Step
              title={t('Autogen_common.r251126.s_Join_the_challenge')}
              label={`${campaign.firstStepRewardAmount} ${campaign.finalRewardCurrency}`}
            />
            <Step
              title={t('Autogen_common.r251126.s_Finish_the_2_step_mission')}
              description={'Step 1 : Invite friends\nStep 2 : Complete trader missions'}
              label={`${Num(campaign.finalRewardAmount).minus(campaign.firstStepRewardAmount).toString()} ${campaign.finalRewardCurrency}`}
            />
            <Step
              title={t('Autogen_common.r251126.s_Claim_campaign_finalRewardAmount_campaign_finalRewardCurrency', {
                campaignFinalRewardAmount: campaign.finalRewardAmount,
                campaignFinalRewardCurrency: campaign.finalRewardCurrency,
              })}
            />
          </div>
          <Details css={{ gridArea: 'details' }} />
          <CtaArea css={{ gridArea: 'cta' }} />
        </div>
      </main>
    </>
  );
};

const Header = ({ css: cssProps }: { css: Styles }) => {
  const { campaignAtom } = useBunja(socialReferralBunja);
  const campaign = useAtomValue(campaignAtom);
  if (!campaign) return null;
  const [start, end] = (() => {
    const start = timestampToDate(campaign.startAt);
    const end = timestampToDate(campaign.endAt);
    const sameYear = isSameYear(start, end);
    const sameMonth = isSameMonth(start, end);
    if (sameYear) {
      if (sameMonth)
        return [
          formatDatetime(start, { format: 'MMM dd', isUTC: true }),
          formatDatetime(end, { format: 'dd, yyyy', isUTC: true }),
        ];
      return [
        formatDatetime(start, { format: 'MMM dd', isUTC: true }),
        formatDatetime(end, { format: 'MMM dd, yyyy', isUTC: true }),
      ];
    }
    return [
      formatDatetime(start, { format: 'MMM dd, yyyy', isUTC: true }),
      formatDatetime(end, { format: 'MMM dd, yyyy', isUTC: true }),
    ];
  })();
  return (
    <div
      className={css(
        stack.raw({
          gap: 8,
          tabletDown: { textAlign: 'center' },
          tablet: { justifyContent: 'center' },
        }),
        cssProps,
      )}
    >
      <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-2' })}>
        {start} ‒ {end}
      </p>
      <p className={css({ textStyle: 'Display/L', color: 'content.default.level-5', whiteSpace: 'pre-wrap' })}>
        {campaign.webLandingPageContent.pageTitlesByStatus['NOT_PARTICIPATED']}
      </p>
    </div>
  );
};

interface StepProps {
  title: string;
  description?: string;
  label?: string;
}
const Step = ({ title, description, label }: StepProps) => {
  return (
    <div
      className={stack({
        position: 'relative',
        gap: 4,
        pl: 24,
        _before: {
          content: '""',
          position: 'absolute',
          w: 1,
          h: 'calc(100% + 32px)',
          left: 4,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          bg: 'border.default.level-1',
        },
        _after: {
          content: '""',
          position: 'absolute',
          w: 8,
          h: 8,
          rounded: 999,
          left: 0,
          top: 10,
          transform: 'translateY(-50%)',
          bg: 'background.default.level-4',
        },
        _last: { _before: { h: 10, top: 0, transform: 'translateX(-50%)' } },
        _first: { _before: { h: 'calc(100% - 10px)', top: 'inherit', bottom: 0, transform: 'translateX(-50%)' } },
      })}
    >
      <div className={hstack({ gap: 16, justify: 'space-between' })}>
        <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>{title}</p>
        {label && (
          <Badge type='outline' color='promotion'>
            {label}
          </Badge>
        )}
      </div>
      {description && (
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2', whiteSpace: 'pre-wrap' })}>
          {description}
        </p>
      )}
    </div>
  );
};

const Details = ({ css: cssProps }: { css: Styles }) => {
  const { campaignAtom } = useBunja(socialReferralBunja);
  const campaign = useAtomValue(campaignAtom);
  if (!campaign) return null;
  return (
    <details
      className={css(
        {
          rounded: 8,
          mt: 12,
          px: 16,
          py: 12,
          color: 'content.default.level-2',
          backgroundColor: 'background.default.level-1',
          _open: {
            '& svg': {
              rotate: '180deg',
            },
          },
        },
        cssProps,
      )}
    >
      <summary className={hstack({ textStyle: 'Text/M/Strong', justifyContent: 'space-between' })}>
        <p>
          <FormattedMessage id='Autogen_common.r251126.s_Promotion_details' />
        </p>
        <ChevronDownIcon size={20} className={css({ transition: 'rotate 0.2s' })} />
      </summary>
      <ul className={css({ textStyle: 'Text/M/Regular', pt: 12, '& > li': { listStyle: 'inside' } })}>
        {campaign.webLandingPageContent.promotionDetails.split('\n').map((content, i) => (
          <li key={i}>{content}</li>
        ))}
      </ul>
    </details>
  );
};

const CtaArea = ({ css: cssProps }: { css: Styles }) => {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const { isDesktop } = useBreakpoint();
  const getUrlWithAnonymousId = useGetUrlWithAnonymousId();
  const code = searchParams.get('code');
  const { campaignAtom } = useBunja(socialReferralBunja);
  const campaign = useAtomValue(campaignAtom);
  if (!campaign) return null;
  return (
    <div
      className={css(
        stack.raw({
          gap: 16,
          bg: 'background.default.level-0',
          tabletDown: { position: 'fixed', left: 0, bottom: 0, p: 16, w: '100%', alignItems: 'center' },
          tablet: { marginBottom: 'auto', pt: 40 },
        }),
        cssProps,
      )}
    >
      <div className={hstack({ gap: 4, color: 'content.accent.promotion' })}>
        <CircleStackIcon size={16} />
        <p className={css({ textStyle: 'Text/M/Regular' })}>
          Join to collect {campaign.firstStepRewardAmount} out of {campaign.finalRewardAmount}{' '}
          {campaign.finalRewardCurrency}
        </p>
      </div>
      <Button
        newTab
        as='a'
        href={
          isDesktop
            ? '/available-on-app'
            : (() => {
                const baseUrl = getUrlWithAnonymousId('SocialReferral');
                const url = new URL(baseUrl);
                url.searchParams.set('deep_link_value', `social-referral/${campaign.campaignName}?code=${code}`);
                return url.toString();
              })()
        }
        label={t('Autogen_common.r251126.s_Join_the_challenge')}
        hierarchy='primary'
        size='large'
        css={{ w: { base: '100%', tablet: 200 } }}
        onClick={() => segment.track(SocialReferralEvent.SocialReferralJoinClick, { campaign_id: campaign.campaignId })}
      />
    </div>
  );
};
